import React from "react";
import { useFormContext } from "react-hook-form";
import {
  AccordionHeader,
  AccordionHeaderText,
  FormController,
  MuiButtonPrimary,
  MuiButtonSecondary,
  MuiGrid,
  MuiSelect,
  MuiSelectWithTextInput,
  MuiTextField,
  OptionsDataType,
} from "../../../../../../../../../../components";
import {
  MAX_LENGTH_15,
  MAX_LENGTH_50,
} from "../../../../../../../../../../helpers/constants";
import { useGridContainer } from "../../../../../../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../../../hooks/useSiteCore";
import { pageId } from "../../../../../../../../../createUser/CreateUser";
import { AddExistingProgramsSearchFormKey } from "../../../../../../../../modelsAndSchemas/Models";
import { addExistingProgramsSiteCoreKeys } from "../../../../../../../../siteCore/SiteCoreKeys";
import { AddExistingProgramsSearchFormWrapper } from "./AddExistingProgramsSearchForm.style";

interface AddExistingProgramsSearchFormProps {
  projectManagerData?: OptionsDataType[];
  handleClearAllSearch: () => void;
  handleSearchClick: () => void;
}

export const AddExistingProgramsSearchForm: React.FC<
  AddExistingProgramsSearchFormProps
> = ({ projectManagerData, handleClearAllSearch, handleSearchClick }) => {
  const { useFetchLabel, commonLabels, fetchDropdownValues } = useSiteCore();
  const { gridSizeLarge: gridSize, gridSizeLargeClass } =
    useGridContainer(true);
  const { commonARIALabel } = useNavigationSiteCore();
  const { control } = useFormContext();
  const {
    addExistingProgramsTitle,
    searchSectionHeader,
    client: clientLabel,
    projectManagerDropDown: projectManagerLabel,
    programOrParticipantName: programOrParticipantNameLabel,
    programKey,
    clientManagedStatusDropDown: clientManagedStatusLabel,
    programStatusDropDown: programStatusLabel,
    programStatusList,
    clientManagedStatusList,
    filterByList,
  } = addExistingProgramsSiteCoreKeys;
  const getLabel = useFetchLabel(addExistingProgramsTitle);
  const getDropdownValues = (listKey: string) =>
    fetchDropdownValues(addExistingProgramsTitle, listKey);
  const filterDropdownValue = getDropdownValues(filterByList);

  return (
    <AddExistingProgramsSearchFormWrapper>
      <AccordionHeader
        showExpandCollapseIcon={false}
        title={<AccordionHeaderText label={getLabel(searchSectionHeader)} />}
      >
        <form data-testid="search-form">
          <MuiGrid container className="form-container">
            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={AddExistingProgramsSearchFormKey.client}
              >
                <MuiTextField
                  label={getLabel(clientLabel)}
                  placeholder={commonLabels.inputPlaceHolder}
                  disabled
                  id={`${pageId}-${AddExistingProgramsSearchFormKey.client}`}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={AddExistingProgramsSearchFormKey.projectManagerId}
              >
                <MuiSelect
                  label={getLabel(projectManagerLabel)}
                  options={projectManagerData ?? []}
                  visibleDefaultSelectOption
                  id={`${pageId}-${AddExistingProgramsSearchFormKey.projectManagerId}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={AddExistingProgramsSearchFormKey.programParticipantName}
              >
                <MuiSelectWithTextInput
                  options={filterDropdownValue}
                  label={getLabel(programOrParticipantNameLabel)}
                  placeholder={commonLabels.inputPlaceHolder}
                  control={control}
                  selectBoxName={
                    AddExistingProgramsSearchFormKey.programParticipantNameFilter
                  }
                  id={`${pageId}-${AddExistingProgramsSearchFormKey.programParticipantName}`}
                  selectBoxId={`${pageId}-${AddExistingProgramsSearchFormKey.programParticipantNameFilter}`}
                  maxLength={MAX_LENGTH_50}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={AddExistingProgramsSearchFormKey.keyNumber}
              >
                <MuiSelectWithTextInput
                  options={filterDropdownValue}
                  label={getLabel(programKey)}
                  placeholder={commonLabels.inputPlaceHolder}
                  control={control}
                  selectBoxName={
                    AddExistingProgramsSearchFormKey.keyNumberFilter
                  }
                  id={`${pageId}-${AddExistingProgramsSearchFormKey.keyNumber}`}
                  selectBoxId={`${pageId}-${AddExistingProgramsSearchFormKey.keyNumberFilter}`}
                  maxLength={MAX_LENGTH_15}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={AddExistingProgramsSearchFormKey.clientManagedStatus}
              >
                <MuiSelect
                  label={getLabel(clientManagedStatusLabel)}
                  options={getDropdownValues(clientManagedStatusList)}
                  visibleDefaultSelectOption
                  id={`${pageId}-${AddExistingProgramsSearchFormKey.clientManagedStatus}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={AddExistingProgramsSearchFormKey.programStatus}
              >
                <MuiSelect
                  label={getLabel(programStatusLabel)}
                  options={getDropdownValues(programStatusList)}
                  visibleDefaultSelectOption
                  id={`${pageId}-${AddExistingProgramsSearchFormKey.programStatus}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
          </MuiGrid>

          <MuiGrid container className={`button-container`}>
            <MuiButtonSecondary
              onClick={handleClearAllSearch}
              id={`${pageId}-clearAll`}
              data-testid="clear-all"
            >
              {commonLabels.clearAll}
            </MuiButtonSecondary>
            <MuiButtonPrimary
              onClick={handleSearchClick}
              id={`${pageId}-search`}
              data-testid="search"
            >
              {commonLabels.search}
            </MuiButtonPrimary>
          </MuiGrid>
        </form>
      </AccordionHeader>
    </AddExistingProgramsSearchFormWrapper>
  );
};
